<script setup lang="ts">
    useSeoMeta({
        title: "比較推奨方針｜株式会社Bang",
        description: "株式会社Bangの比較推奨方針を掲載しています。",
        ogTitle: "比較推奨方針｜株式会社Bang",
        ogDescription: "株式会社Bangの比較推奨方針を掲載しています。",
        ogUrl: "https://www.bang-inc.jp/info/policy-sales",
    });

    const breadcrumbsItems = [
        { text: "トップページ", href: "/" },
        { text: "比較推奨方針", href: "/info/policy-sales" },
    ];
</script>

<template>
  <main class="policy-sales">
    <!-- パンくずリスト -->
    <Breadcrumbs :items="breadcrumbsItems" />
    <div class="policy-sales__body">
        <HeadingCorp :level="1">比較推奨方針</HeadingCorp>

        <!-- 第１章 -->
        <section class="policy-sales__section">
            <HeadingCorp :level="2">第１章　総則</HeadingCorp>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">１．目的</HeadingCorp>
                <p class="policy-sales__section-text">本方針は、株式会社Bang（以下「当社」という。）が行う保険募集に関連する業務において、お客さまの意向を適切に把握したうえで、お客さま本位の業務運営のもと、客観的かつ合理的な基準に基づく比較説明および推奨販売を適切に実施するための基本的な考え方および取扱いを定めるものである。</p>
                <p class="policy-sales__section-text">当社は、保険業法第294条の３に定める保険募集人の体制整備義務、金融サービスの提供及び利用環境の整備等に関する法律に定める誠実義務、および「保険会社向けの総合的な監督指針」Ⅱ－４－２－９に定める比較推奨販売に関する規律を遵守し、お客さま本位の業務運営を実践する。</p>
            </div>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">２．適用範囲（対象チャネル）</HeadingCorp>
                <p class="policy-sales__section-text">当社は、事業の発展段階に応じて、以下の３つのチャネルを通じて保険商品の比較・推奨を行う、または将来行うことを予定している。本方針は、現在稼働中のチャネルのみならず、将来展開を予定するチャネルについても、あらかじめ整備すべき事項を定めるものとする。</p>
                <ul>
                    <li class="policy-sales__definition-item">1. WEBチャネル：当社WEBサイト上でお客さま自身が保険商品を比較・選択し、申込みまで完結する形態（現行実施）</li>
                    <li class="policy-sales__definition-item">2. コールセンター（意向把握・確認およびアポイントメント取得）チャネル：当社が電話等によりお客さまの意向を把握・確認したうえで、提携代理店による対面・電話等での保険募集に向けたアポイントメントを取得する形態（今後整備）</li>
                    <li class="policy-sales__definition-item">3. 提携代理店との募集連携形態：上記２．のアポイントメント取得を前提に、募集実務（契約締結の媒介・代理、告知受領等）は提携代理店が行い、当社は意向把握・確認およびアポイントメント取得を担う、当社と提携代理店による共同募集の形態（今後整備。上記１．のWEB完結と併存する）</li>
                </ul>
            </div>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">３．用語の定義</HeadingCorp>
                <ul class="policy-sales__dot-list">
                    <li class="policy-sales__dot-item">・比較説明（イ方式）：複数の保険契約の内容を比較して説明する場合において、比較する事項・内容を適切に説明し、お客さまが契約内容について正確な判断を行うために必要な事項を包括的に示す方式（保険業法施行規則第227条の２第３項第４号イ）</li>
                    <li class="policy-sales__dot-item">・推奨販売（ロ方式）：二以上の比較可能な同種の保険契約の中から、お客さまの意向に沿って保険契約を選別し、一または二以上の保険契約を提示・推奨する方式（同号ロ）。当社は、商品特性・保険料水準等の客観的基準によらず、代理店手数料の水準や資本関係等の当社の事情のみに基づいて商品を絞り込み、または特定の商品を提示・推奨すること（いわゆるハ方式）は行わない。</li>
                    <li class="policy-sales__dot-item">・意向把握・確認：保険業法第294条の２に基づき、お客さまの意向（保障内容、保険期間、保険料水準、契約の目的等）を把握し、提案内容がお客さまの意向に沿ったものであることを最終的に確認する行為</li>
                    <li class="policy-sales__dot-item">・共同募集：一つの保険契約の募集プロセスについて、当社と提携代理店がそれぞれ役割を分担して関与する募集形態</li>
                    <li class="policy-sales__dot-item">・提携代理店：当社との間で業務委託契約その他の提携契約を締結し、当社が取得したアポイントメントに基づき、実際の保険募集実務（契約締結の媒介・代理、告知受領等）を行う保険代理店</li>
                </ul>
            </div>
        </section>

        <!-- 第２章 -->
        <section class="policy-sales__section">
            <HeadingCorp :level="2">第２章　比較推奨に関する基本原則</HeadingCorp>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">１．お客さまの最善の利益優先の原則</HeadingCorp>
                <p class="policy-sales__section-text">当社は、比較説明または推奨販売のいずれの方式による場合も、お客さま本位の業務運営のもと、お客さまに対して誠実かつ公正にその業務を遂行する。</p>
                <p class="policy-sales__section-text">当社の代理店手数料の多寡、保険会社との資本関係、事務手続上の都合その他当社の事情によって、商品の絞り込みや推奨内容が左右されることはない。</p>
            </div>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">２．客観的・合理的な基準に基づく選定</HeadingCorp>
                <p class="policy-sales__section-text">当社が特定の商品を推奨する場合は、お客さまの意向との関係で、当該商品をどのように選別したかについて、合理的かつ一定の具体性を有する基準・理由（保障内容、保険料水準、特約の内容、継続性、事務手続の利便性等）を説明する。あわせて、推奨する商品以外にも比較対象となる商品があること、および、お客さまの求めに応じてそれらの商品の概要・内容を説明する旨をご案内する。</p>
            </div>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">３．説明義務の徹底</HeadingCorp>
                <p class="policy-sales__section-text">複数商品の内容を比較して説明する場合（比較説明）には、比較する事項・内容を適切に説明するとともに、お客さまが正確な判断を行うために必要な事項を包括的に示す。特定商品の優位性を示すために他の商品と比較する場合は、比較対象商品についてもその全体像・特性を正確に示したうえで、推奨する商品の優位性の根拠を説明する。</p>
            </div>
        </section>

        <!-- 第３章 -->
        <section class="policy-sales__section">
            <HeadingCorp :level="2">第３章　チャネル別の比較推奨方針</HeadingCorp>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">１．WEBチャネルによる比較説明（現行）</HeadingCorp>
                <div class="policy-sales__section-block">
                    <HeadingCorp :level="4">（１）掲載商品の選定基準</HeadingCorp>
                    <ul class="policy-sales__dot-list">
                        <li class="policy-sales__dot-item">・当社が代理店委託を受けている保険会社のうち、当該保険会社からWEB掲載の許可が得られた商品・プランを掲載する。なお、掲載商品の選定にあたっては、保険会社との資本関係、取引条件または代理店手数料の水準によって商品の掲載可否を決定しない。</li>
                        <li class="policy-sales__dot-item">・掲載にあたっては、各社の保険料水準、補償内容、特約、免責事項等の比較事項を、特定商品に偏ることなく、お客さまが正確な判断を行うために必要な情報として包括的に表示する。</li>
                        <li class="policy-sales__dot-item">・WEBサイトに掲載がない商品であっても、既契約の継続・乗り換えに関するご案内、または追加契約となる商品のご案内を行う場合がある。この場合も、当該商品を選定した理由を明確にする。</li>
                    </ul>
                </div>
                <div class="policy-sales__section-block">
                    <HeadingCorp :level="4">（２）「おすすめ」表示等、当社が商品を絞り込んで提示する機能を設ける場合の取扱い</HeadingCorp>
                    <p class="policy-sales__section-text">当社WEBサイトにおいて、お客さまの入力情報（年齢・車種・補償ニーズ等）に基づき商品を並び替え、または一部の商品を優先表示する機能を提供する場合、当該表示は前章に定める推奨販売（ロ方式）に該当するものとして取り扱う。この場合、当社は、並び替え・絞り込みに用いる基準（保険料水準、補償内容の充足度等の客観的指標）をあらかじめ社内規則に定め、当該基準および表示の趣旨をお客さまにわかりやすく開示する。</p>
                </div>
            </div>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">２．コールセンターによる意向把握・意向確認およびアポイントメント取得（新設）</HeadingCorp>
                <div class="policy-sales__section-block">
                    <HeadingCorp :level="4">（１）位置づけ</HeadingCorp>
                    <p class="policy-sales__section-text">当社は、WEBでの完結が難しいお客さま、電話でのご相談を希望されるお客さま等に対し、コールセンターにおいて意向把握・意向確認を行ったうえで、提携代理店による募集手続きへのアポイントメントを取得するサービスを整備する。</p>
                    <p class="policy-sales__section-text">当社のコールセンター業務は、お客さまの意向把握・意向確認およびアポイントメント取得を行うものであり、契約締結の媒介・代理、告知受領その他の募集実務は提携代理店が行う。なお、当社がお客さまに対し商品選択に影響を与える説明または案内を行う場合には、本方針に定める比較説明・推奨販売に関する規律を適用する。</p>
                </div>
                <div class="policy-sales__section-block">
                    <HeadingCorp :level="4">（２）意向把握・確認のプロセス</HeadingCorp>
                    <ul class="policy-sales__dot-list">
                        <li class="policy-sales__dot-item">・お客さまの年齢、家族構成、資産状況、既契約の有無、金融商品に関する知識・経験、保険加入を検討する目的等を確認し、お客さまの意向（重視する保障内容・保険料水準等）を丁寧かつ明確に把握する。</li>
                        <li class="policy-sales__dot-item">・意向把握・確認およびご案内に用いるトークスクリプト・チェックシートを整備し、募集人（意向把握等を行う担当者を含む）が対応時にいつでも確認できる体制とする。</li>
                    </ul>
                </div>
                <div class="policy-sales__section-block">
                    <HeadingCorp :level="4">（３）アポイントメント取得と提携代理店への引継ぎ</HeadingCorp>
                    <ul class="policy-sales__dot-list">
                        <li class="policy-sales__dot-item">・把握したお客さまの意向を記録し、個人情報保護法その他の関係法令および当社プライバシーポリシーに従い、必要な同意取得その他の措置を講じたうえで、正確かつ遅滞なく提携代理店に引き継ぐ。</li>
                        <li class="policy-sales__dot-item">・引継ぎ内容は、提携代理店が重複した意向把握を要しない程度に具体的なものとする。</li>
                        <li class="policy-sales__dot-item">・アポイントメント取得段階で、お客さまに対し、実際の契約締結の媒介・代理は提携代理店が行うこと、当社は契約締結の代理権・告知受領権を有しないことを明確に説明する。</li>
                        <li class="policy-sales__dot-item">・引継ぎ後、提携代理店における募集内容（推奨商品・説明状況等）について、当社と提携代理店との間で定期的に情報共有し、お客さまの意向との整合性を確認する。</li>
                    </ul>
                </div>
            </div>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">３．提携代理店との共同募集における役割分担</HeadingCorp>
                <div class="policy-sales__section-block">
                    <HeadingCorp :level="4">（１）役割分担の明確化</HeadingCorp>
                    <div class="policy-sales__table-wrapper">
                        <table class="policy-sales__table">
                            <thead>
                                <tr>
                                    <th>実施主体</th>
                                    <th>主な行為</th>
                                    <th>適用ルール・留意点</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>当社（Bang）</td>
                                    <td>WEB掲載による比較説明<br>コールセンターでの意向把握・意向確認<br>アポイントメント取得</td>
                                    <td>比較説明（イ方式）の規律を適用<br>契約締結の代理権・告知受領権は有しない旨を明示</td>
                                </tr>
                                <tr>
                                    <td>提携代理店</td>
                                    <td>契約締結の媒介・代理<br>重要事項説明・告知受領<br>契約手続き・アフターフォロー</td>
                                    <td>当社からの引継ぎ内容（意向）との整合性を確認したうえで募集を実施<br>提携代理店固有の比較推奨方針・体制整備義務を別途負う</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
                <div class="policy-sales__section-block">
                    <HeadingCorp :level="4">（２）重複回避と一貫性の確保</HeadingCorp>
                    <ul class="policy-sales__dot-list">
                        <li class="policy-sales__dot-item">・お客さまに対する説明が、当社と提携代理店との間で齟齬・重複することのないよう、意向情報の引継ぎフォーマットを統一し、必要に応じて双方合議のうえ運用ルールを整備する。</li>
                        <li class="policy-sales__dot-item">・提携代理店の選定にあたっては、提携代理店の取扱商品数・体制整備状況・過去の苦情発生状況等を確認し、お客さまの商品選択の機会を狭めることのないよう配慮する。</li>
                    </ul>
                </div>
                <div class="policy-sales__section-block">
                    <HeadingCorp :level="4">（３）便宜供与・利益相反の防止</HeadingCorp>
                    <ul class="policy-sales__dot-list">
                        <li class="policy-sales__dot-item">・当社は、提携代理店の選定・アポイントメント配分にあたり、提携代理店から手数料の水準以外の金銭的・非金銭的な便宜供与を求めない。また、特定の提携代理店を優先的に扱うことの見返りとして便宜供与を受け入れない。</li>
                        <li class="policy-sales__dot-item">・提携代理店との間の対価（送客手数料等）の設定は、募集実務の対価として合理的な範囲にとどめ、お客さまへの商品推奨の公正性に影響を及ぼさない方法により行う。</li>
                    </ul>
                </div>
                <div class="policy-sales__section-block">
                    <HeadingCorp :level="4">（４）苦情対応・責任分担</HeadingCorp>
                    <ul class="policy-sales__dot-list">
                        <li class="policy-sales__dot-item">・意向把握・アポイントメント取得段階に起因する苦情は当社が、契約締結・告知受領段階に起因する苦情は提携代理店が一次対応する体制とし、あらかじめ提携契約にて責任分担を明確化する。</li>
                        <li class="policy-sales__dot-item">・苦情内容は当社・提携代理店双方で共有し、比較推奨プロセスの改善に活用する。</li>
                    </ul>
                </div>
            </div>
        </section>

        <!-- 第４章 -->
        <section class="policy-sales__section">
            <HeadingCorp :level="2">第４章　推奨基準・推奨理由の説明</HeadingCorp>
            <p class="policy-sales__section-text">当社は、いずれのチャネルにおいても、特定の保険契約を推奨する場合には、以下の点を踏まえた説明を行う。</p>
            <ul>
                <li class="policy-sales__definition-item">1. お客さまの意向（重視する補償内容、保険料水準、契約期間、特約の要否等）との適合性</li>
                <li class="policy-sales__definition-item">2. 商品特性・保険料水準等、客観的かつ一定の具体性を有する基準・理由（保険会社との資本関係、代理店手数料の水準、当社の事務手続上の都合その他当社の事情のみに基づくものは含まない）</li>
                <li class="policy-sales__definition-item">3. 推奨する商品以外にも比較対象となる商品が存在する旨、および、お客さまの求めに応じてその概要・内容を説明する旨</li>
                <li class="policy-sales__definition-item">4. 乗り換え・解約を伴う提案の場合は、あわせて不利益となり得る事項（保障の空白期間、解約返戻金の減少等）</li>
            </ul>
        </section>

        <!-- 第５章 -->
        <section class="policy-sales__section">
            <HeadingCorp :level="2">第５章　利益相反の防止・便宜供与の排除</HeadingCorp>
            <p class="policy-sales__section-text">当社は、保険会社間の代理店手数料の水準の相違により商品推奨が影響を受けることのないよう、手数料水準にかかわらずお客さまの意向に沿った商品を選定する。また、保険会社および提携代理店から、当社の比較推奨販売に影響を及ぼし得る過度な便宜供与を求めず、受け入れない。保険会社等からの便宜供与の実績と当社における当該保険会社の商品の取扱いシェアとの間に有意な変化が認められないかを、新商品の発売等他の要因も考慮しつつ定期的に確認・検証する。</p>
        </section>

        <!-- 第６章 -->
        <section class="policy-sales__section">
            <HeadingCorp :level="2">第６章　体制整備（社内規則・教育・モニタリング）</HeadingCorp>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">１．社内規則の整備（Plan）</HeadingCorp>
                <ul class="policy-sales__dot-list">
                    <li class="policy-sales__dot-item">・本方針および商品選定・推奨に係る基準を、コンプライアンスマニュアル・業務マニュアルに具体的に定める。</li>
                    <li class="policy-sales__dot-item">・コールセンター業務の開始にあたり、意向把握・確認およびアポイントメント取得に関するトークスクリプト・チェックシート・引継ぎフォーマットを整備する。</li>
                    <li class="policy-sales__dot-item">・提携代理店との共同募集開始にあたり、役割分担・情報連携・便宜供与防止・苦情対応に関する提携契約および運用細則を整備する。</li>
                </ul>
            </div>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">２．教育・管理・指導（Do）</HeadingCorp>
                <ul class="policy-sales__dot-list">
                    <li class="policy-sales__dot-item">・募集人（意向把握等を行う担当者を含む）に対し、比較説明・推奨販売の区分、推奨理由の説明方法についてEラーニング・研修を実施し、理解度を確認する。</li>
                    <li class="policy-sales__dot-item">・提携代理店に対しても、当社からの引継ぎ内容に基づく募集が適切に行われるよう、必要な情報提供・連携を行う。</li>
                </ul>
            </div>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">３．確認・検証（Check）</HeadingCorp>
                <ul class="policy-sales__dot-list">
                    <li class="policy-sales__dot-item">・お客さまの意向、絞り込みの理由（推奨理由）、比較提示した商品、最終的な申込商品の一連の流れが確認できる記録を作成・保存し、コンプライアンス部門が定期的に分析・検証する。</li>
                    <li class="policy-sales__dot-item">・WEBサイトにおける表示ロジック（絞り込み・並び替え等）についても、定期的に基準への適合性を確認する。</li>
                </ul>
            </div>
            <div class="policy-sales__section-box">
                <HeadingCorp :level="3">４．改善（Act）</HeadingCorp>
                <ul class="policy-sales__dot-list">
                    <li class="policy-sales__dot-item">・確認・検証の結果、比較推奨販売の適切性に問題が認められた場合は、社長および関係部門に報告のうえ、社内規則・トークスクリプト・提携契約等の見直しを行い、PDCAサイクルによる継続的な改善を図る。</li>
                </ul>
            </div>
        </section>

        <!-- 第７章 -->
        <section class="policy-sales__section">
            <HeadingCorp :level="2">第７章　記録の保存</HeadingCorp>
            <p class="policy-sales__section-text">当社は、比較推奨販売の適切性に関する確認・検証に必要な記録・証跡（意向把握内容、推奨理由、引継ぎ記録、通話録音、苦情記録等）について、法令または社内規則に定める保存期間、適切に保存する。</p>
        </section>

        <!-- 第８章 -->
        <section class="policy-sales__section">
            <HeadingCorp :level="2">第８章　見直し・改定</HeadingCorp>
            <p class="policy-sales__section-text">本方針は、関連法令（保険業法、同施行規則）および監督指針の改正、当社の事業展開（コールセンターの新設、提携代理店との共同募集の開始、将来的な自社募集体制の整備等）に応じて、必要な見直しを行う。</p>
        </section>

        <!-- 制定日 -->
        <section class="policy-sales__section">
            <p class="policy-sales__section-text">
                制定日：2026年10月1日<br>
                株式会社Bang　代表取締役　長山　真己
            </p>
        </section>
    </div>
  </main>
</template>

<style scoped lang="scss">
.policy-sales {
    position: relative;
    width: min(91vw, 64.5rem);
    margin: auto;
    &::before {
        content: '';
        position: absolute;
        width: 20rem; // 320px
        height: 20rem; // 320px
        aspect-ratio: 1/1;
        left: -4rem; // -64px
        top: -3rem; // -48px
        border-radius: 20rem; // 320px
        box-sizing: border-box;
        border: 1.5rem solid #EFF7FF; // 24px
        z-index: -1; // コンテンツの背景に配置
        pointer-events: none; // クリックイベントを通す
    }
    &__body {
        padding: 2rem 0 4.5rem;
    }
    &__section {
        line-height: 150%;
        &:not(:last-child) {
            margin-bottom: 2.5rem;
        }
        a[href] {
            color: #2174C0;
            text-decoration: underline;
            overflow-wrap: anywhere;
        }
    }
    &__section-box {
        &:not(:last-child) {
            margin-bottom: 1rem;
        }
    }
    &__section-block {
        &:not(:last-child) {
            margin-bottom: 1rem;
        }
    }
    &__section-text {
        &:not(:last-child) {
            margin-bottom: 0.25rem;
        }
    }
    &__dot-list {
        margin-bottom: 0.25rem;
    }
    &__dot-item {
        margin-bottom: 0.25rem;
        text-indent: -1rem;
        padding-left: 1rem;
    }
    &__definition-item {
        margin-bottom: 0.5rem;
        text-indent: -1.2rem;
        padding-left: 1.2rem;
    }
    &__table-wrapper {
        overflow-x: auto;
        margin-bottom: 0.5rem;
    }
    &__table {
        width: 100%;
        border-collapse: collapse;
        th, td {
            border: 1px solid #C8D8E8;
            padding: 0.5rem 0.75rem;
            vertical-align: top;
            line-height: 150%;
        }
        th {
            background-color: #EFF7FF;
            font-weight: bold;
            text-align: left;
        }
    }
}

@include mixin.media(pc) {
    .policy-sales {
        &::before {
            width: 32.5rem; // 520px
            height: 32.5rem; // 520px
            left: -10.7rem; // -171.2px
            top: -4rem; // -64px
            border-radius: 32.5rem; // 520px
            border: 2.5rem solid #EFF7FF; // 24px
        }
        &__body {
            padding: 4rem 0 5rem;
        }
        &__section {
            &:not(:last-child) {
                margin-bottom: 3rem;
            }
        }
    }
}
</style>
